import React from "react";
import { Control, FieldValues, FieldPath } from "react-hook-form";
import {
  DoubleMaterialityStakeholderImpactArea,
  StakeholderDto,
} from "@green0meter/api-client";

import SelectableChip from "../SelectableChip/SelectableChip";

import { Stack, Typography, FormControl, Select, MenuItem } from "@components/base";
import LabeledControllerField from "@components/common/LabeledControllerField/LabeledControllerField";

// Label and color mapping for impact areas
const impactAreaMap: Record<DoubleMaterialityStakeholderImpactArea, string> = {
  [DoubleMaterialityStakeholderImpactArea.Risk]: "Risks",
  [DoubleMaterialityStakeholderImpactArea.Opportunity]: "Opportunities",
  [DoubleMaterialityStakeholderImpactArea.Impact]: "Impact",
} as const;

const impactAreaColors: Record<
  DoubleMaterialityStakeholderImpactArea,
  { background: string; border: string; color: string }
> = {
  [DoubleMaterialityStakeholderImpactArea.Risk]: {
    background: "#F5E6B3",
    border: "#F3C186",
    color: "#DE8217",
  },
  [DoubleMaterialityStakeholderImpactArea.Opportunity]: {
    background: "#E6F5ED",
    border: "#C4E5D2",
    color: "#2B8B58",
  },
  [DoubleMaterialityStakeholderImpactArea.Impact]: {
    background: "#F5F5F5",
    border: "#E9E9E9",
    color: "#757575",
  },
} as const;

interface ImpactAreasFieldProps<
  T extends FieldValues & { impactAreas: StakeholderDto["impactAreas"] },
> {
  control: Control<T, Record<string, unknown>, unknown>;
  disabled?: boolean;
}

const ImpactAreasField = <
  T extends FieldValues & { impactAreas: StakeholderDto["impactAreas"] },
>({
  control,
  disabled = false,
}: ImpactAreasFieldProps<T>): React.ReactElement => {
  return (
    <LabeledControllerField<T, FieldPath<T>>
      label={"IRO"}
      name={"impactAreas" as FieldPath<T>}
      control={control}
      disabled={disabled}
      render={({ field, error, disabled }) => (
        <FormControl fullWidth error={Boolean(error)}>
          <Select
            {...field}
            multiple
            disabled={disabled}
            variant="filled"
            renderValue={(selected) => (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(selected as DoubleMaterialityStakeholderImpactArea[]).map(
                  (value) => {
                    const colors = impactAreaColors[value];

                    // Skip rendering if value is not found in the mapping
                    if (!colors || !impactAreaMap[value]) {
                      console.warn(`Unknown impact area: ${value}`);

                      return null;
                    }

                    return (
                      <SelectableChip
                        key={value}
                        label={impactAreaMap[value]}
                        backgroundColor={colors.background}
                        borderColor={colors.border}
                        textColor={colors.color}
                        size="medium"
                        onDelete={() => {
                          const newSelected = (
                            field.value as DoubleMaterialityStakeholderImpactArea[]
                          ).filter((item) => item !== value);

                          field.onChange(newSelected);
                        }}
                      />
                    );
                  },
                )}
              </Stack>
            )}
          >
            {Object.values(DoubleMaterialityStakeholderImpactArea).map((area) => (
              <MenuItem key={area} value={area}>
                {impactAreaMap[area]}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {error.message}
            </Typography>
          )}
        </FormControl>
      )}
    />
  );
};

export default ImpactAreasField;
